/**
 * Secure Authentication Utilities for Admin Dashboard
 * Implements enterprise-grade security patterns for token management
 */

// Security constants
const STORAGE_KEYS = {
  ENCRYPTED_TOKEN: 'admin_secure_token',
  SESSION_DATA: 'admin_session',
  CSRF_TOKEN: 'admin_csrf',
  ACTIVITY_TIMESTAMP: 'admin_activity',
};

const SECURITY_CONFIG = {
  SESSION_TIMEOUT: 8 * 60 * 60 * 1000, // 8 hours in milliseconds
  INACTIVITY_TIMEOUT: 2 * 60 * 60 * 1000, // 2 hours in milliseconds
  TOKEN_REFRESH_THRESHOLD: 10 * 60 * 1000, // Refresh 10 minutes before expiry
  ACTIVITY_CHECK_INTERVAL: 5 * 60 * 1000, // Check activity every 5 minutes
  MAX_TOKEN_AGE: 24 * 60 * 60 * 1000, // 24 hours maximum token age
};

/**
 * Simple encryption/decryption using browser crypto API
 * Note: This provides basic protection against casual XSS, not military-grade security
 */
class TokenCrypto {
  constructor() {
    this.algorithm = 'AES-GCM';
    this.keyLength = 256;
  }

  // Generate session-persistent encryption key
  async generateKey() {
    const encoder = new TextEncoder();
    
    // Try to get existing session key material
    let keyMaterial = localStorage.getItem('admin_session_key');
    let salt = localStorage.getItem('admin_session_salt');
    
    if (!keyMaterial || !salt) {
      // Generate new session-unique key material (only for new sessions)
      const sessionSeed = crypto.getRandomValues(new Uint32Array(8)).join('-');
      keyMaterial = `2W-ADMIN-SESSION-${sessionSeed}`;
      
      // Generate session-unique salt
      const randomSalt = crypto.getRandomValues(new Uint8Array(16));
      salt = btoa(String.fromCharCode(...randomSalt));
      
      // Store for this session (will be cleared on logout)
      localStorage.setItem('admin_session_key', keyMaterial);
      localStorage.setItem('admin_session_salt', salt);
    }
    
    // Convert salt back to Uint8Array
    const saltBytes = new Uint8Array(atob(salt).split('').map(c => c.charCodeAt(0)));
    
    const cryptoKeyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(keyMaterial),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBytes,
        iterations: 100000,
        hash: 'SHA-256'
      },
      cryptoKeyMaterial,
      { name: this.algorithm, length: this.keyLength },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Encrypt token data
  async encrypt(data) {
    try {
      const key = await this.generateKey();
      const encoder = new TextEncoder();
      const encodedData = encoder.encode(JSON.stringify(data));
      
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encrypted = await crypto.subtle.encrypt(
        { name: this.algorithm, iv },
        key,
        encodedData
      );

      // Combine iv and encrypted data
      const result = new Uint8Array(iv.length + encrypted.byteLength);
      result.set(iv);
      result.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...result));
    } catch (error) {
      console.error('Token encryption failed - no fallback allowed for security:', error);
      throw new Error('Token encryption failed - cannot proceed without encryption');
    }
  }

  // Decrypt token data
  async decrypt(encryptedData) {
    try {
      const key = await this.generateKey();
      const data = new Uint8Array(atob(encryptedData).split('').map(char => char.charCodeAt(0)));
      
      const iv = data.slice(0, 12);
      const encrypted = data.slice(12);

      const decrypted = await crypto.subtle.decrypt(
        { name: this.algorithm, iv },
        key,
        encrypted
      );

      const decoder = new TextDecoder();
      return JSON.parse(decoder.decode(decrypted));
    } catch (error) {
      console.error('Token decryption failed - clearing compromised data:', error);
      // Clear potentially corrupted data
      localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_TOKEN);
      return null;
    }
  }
}

// Initialize crypto instance
const tokenCrypto = new TokenCrypto();

/**
 * Secure Authentication Manager
 */
export class SecureAuthManager {
  constructor() {
    this.activityTimer = null;
    this.tokenRefreshTimer = null;
    this.sessionWarningTimer = null;
    this.eventMap = new Map();
    
    // Start activity monitoring
    this.startActivityMonitoring();
    
    // Check for existing session on startup
    this.validateExistingSession();
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.eventMap.has(event)) {
      this.eventMap.set(event, new Set());
    }
    
    this.eventMap.get(event).add(callback);
  }

  removeEventListener(event, callback) {
    if (this.eventMap && this.eventMap.has(event)) {
      this.eventMap.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.eventMap && this.eventMap.has(event)) {
      this.eventMap.get(event).forEach(callback => {
        try {
          callback({ detail: data });
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }

  // Secure token storage
  async storeToken(token, userData) {
    try {
      const timestamp = Date.now();
      const tokenData = {
        token,
        userData,
        timestamp,
        expiresAt: timestamp + SECURITY_CONFIG.MAX_TOKEN_AGE,
      };

      const encryptedToken = await tokenCrypto.encrypt(tokenData);
      
      // Store encrypted token
      localStorage.setItem(STORAGE_KEYS.ENCRYPTED_TOKEN, encryptedToken);
      
      // Store minimal session metadata (non-PII only)
      localStorage.setItem(STORAGE_KEYS.SESSION_DATA, JSON.stringify({
        sessionId: crypto.getRandomValues(new Uint32Array(2)).join(''),
        loginTime: timestamp,
        lastActivity: timestamp,
      }));

      // Update activity timestamp
      this.updateActivity();
      
      // Schedule token refresh
      this.scheduleTokenRefresh(tokenData);
      
      return true;
    } catch (error) {
      console.error('Failed to store secure token:', error);
      return false;
    }
  }

  // Retrieve and decrypt token
  async getToken() {
    try {
      const encryptedToken = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_TOKEN);
      if (!encryptedToken) return null;

      const tokenData = await tokenCrypto.decrypt(encryptedToken);
      if (!tokenData) return null;

      // Check if token is expired
      if (Date.now() > tokenData.expiresAt) {
        console.warn('Token expired, clearing session');
        this.clearSession();
        return null;
      }

      return tokenData.token;
    } catch (error) {
      console.error('Failed to retrieve token:', error);
      this.clearSession();
      return null;
    }
  }

  // Get user data
  async getUserData() {
    try {
      const encryptedToken = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_TOKEN);
      if (!encryptedToken) return null;

      const tokenData = await tokenCrypto.decrypt(encryptedToken);
      return tokenData?.userData || null;
    } catch (error) {
      console.error('Failed to retrieve user data:', error);
      return null;
    }
  }

  // Update activity timestamp
  updateActivity() {
    const timestamp = Date.now();
    localStorage.setItem(STORAGE_KEYS.ACTIVITY_TIMESTAMP, timestamp.toString());
    
    // Update session data
    const sessionData = this.getSessionData();
    if (sessionData) {
      sessionData.lastActivity = timestamp;
      localStorage.setItem(STORAGE_KEYS.SESSION_DATA, JSON.stringify(sessionData));
    }

    // Emit activity event
    this.emit('activity', { timestamp });
  }

  // Get session metadata
  getSessionData() {
    try {
      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error('Failed to parse session data:', error);
      return null;
    }
  }

  // Check if session is valid
  isSessionValid() {
    const sessionData = this.getSessionData();
    if (!sessionData) return false;

    const now = Date.now();
    const lastActivity = sessionData.lastActivity || 0;
    const loginTime = sessionData.loginTime || 0;

    // Check session timeout
    if (now - loginTime > SECURITY_CONFIG.SESSION_TIMEOUT) {
      return false;
    }

    // Check inactivity timeout
    if (now - lastActivity > SECURITY_CONFIG.INACTIVITY_TIMEOUT) {
      return false;
    }

    return true;
  }

  // Clear all session data
  clearSession() {
    // Clear all storage
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear session key material (added for session-persistent keys)
    localStorage.removeItem('admin_session_key');
    localStorage.removeItem('admin_session_salt');

    // Clear timers
    if (this.activityTimer) clearInterval(this.activityTimer);
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);
    if (this.sessionWarningTimer) clearTimeout(this.sessionWarningTimer);

    // Emit logout event
    this.emit('logout', { reason: 'session_cleared' });
  }

  // Activity monitoring
  startActivityMonitoring() {
    // Monitor user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      this.updateActivity();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Periodic session validation
    this.activityTimer = setInterval(() => {
      this.validateSession();
    }, SECURITY_CONFIG.ACTIVITY_CHECK_INTERVAL);
  }

  // Validate current session
  async validateSession() {
    const token = await this.getToken();
    const isValid = token && this.isSessionValid();
    
    if (!isValid) {
      console.warn('Session validation failed');
      this.clearSession();
      return false;
    }

    // Check for session expiration warning
    const sessionData = this.getSessionData();
    if (sessionData) {
      const timeLeft = (sessionData.lastActivity + SECURITY_CONFIG.INACTIVITY_TIMEOUT) - Date.now();
      
      // Warn 5 minutes before expiration
      if (timeLeft < 5 * 60 * 1000 && timeLeft > 0) {
        this.emit('session_warning', { timeLeft });
      }
    }

    return true;
  }

  // Validate existing session on startup
  async validateExistingSession() {
    const isValid = await this.validateSession();
    if (isValid) {
      const userData = await this.getUserData();
      this.emit('session_restored', { userData });
    }
  }

  // Schedule token refresh (placeholder for future implementation)
  scheduleTokenRefresh(tokenData) {
    const timeUntilRefresh = tokenData.expiresAt - Date.now() - SECURITY_CONFIG.TOKEN_REFRESH_THRESHOLD;
    
    if (timeUntilRefresh > 0) {
      this.tokenRefreshTimer = setTimeout(() => {
        this.emit('token_refresh_needed', { tokenData });
      }, timeUntilRefresh);
    }
  }

  // Generate CSRF token
  generateCSRFToken() {
    const token = crypto.getRandomValues(new Uint32Array(4)).join('-');
    localStorage.setItem(STORAGE_KEYS.CSRF_TOKEN, token);
    return token;
  }

  // Get CSRF token
  getCSRFToken() {
    return localStorage.getItem(STORAGE_KEYS.CSRF_TOKEN);
  }

  // Validate CSRF token
  validateCSRFToken(token) {
    return token === this.getCSRFToken();
  }
}

// Export singleton instance
export const secureAuth = new SecureAuthManager();

// Utility functions for backward compatibility
export const secureAuthUtils = {
  // Secure storage utilities
  setToken: async (token, userData) => {
    return await secureAuth.storeToken(token, userData);
  },

  getToken: async () => {
    return await secureAuth.getToken();
  },

  getUserData: async () => {
    return await secureAuth.getUserData();
  },

  clearSession: () => {
    secureAuth.clearSession();
  },

  // Session validation
  isSessionValid: () => {
    return secureAuth.isSessionValid();
  },

  // Activity tracking
  updateActivity: () => {
    secureAuth.updateActivity();
  },

  // CSRF protection
  generateCSRFToken: () => {
    return secureAuth.generateCSRFToken();
  },

  getCSRFToken: () => {
    return secureAuth.getCSRFToken();
  },
};

// Export for debugging in development
if (process.env.NODE_ENV === 'development') {
  window.secureAuthDebug = {
    manager: secureAuth,
    crypto: tokenCrypto,
    config: SECURITY_CONFIG,
  };
}